/**
 * Identidade do contato de WhatsApp no painel: telefone e/ou BSUID.
 *
 * O **BSUID** (*business scoped user id*) é o identificador que a Meta
 * introduziu para o usuário final — ex. `IN.1648738043044942`. Com ele o
 * telefone deixou de ser garantido: um contato que entrou por QR Code, link
 * `wa.me` ou anúncio Click-to-WhatsApp pode não ter número nenhum.
 *
 * Espelha a semântica de `api-whatsapp/src/infra/utils/identity/whatsapp_identity.ts`.
 * A regra que **não pode** divergir: o bsuid é preservado *verbatim* (só `trim`).
 * Aplicar `replace(/\D/g,'')` nele — como se faz com telefone em vários pontos
 * deste repo — transformaria `IN.1648738043044942` num "telefone" de 16 dígitos
 * inventado.
 */

/** Formato do BSUID: duas letras de país, ponto, dígitos. Ex. `IN.164…`, `BR.191…`. */
export const BSUID_PATTERN = /^[A-Za-z]{2}\.\d+$/

export interface WhatsappIdentity {
  phone?: string
  bsuid?: string
}

export interface IdentityDisplay {
  /** Texto pronto para renderizar: `+5548988241441` ou `IN.••••4942`. */
  text: string
  /** `true` quando o texto exibido é um bsuid mascarado. */
  isBsuid: boolean
  /** `true` quando o contato não tem telefone — pede aviso explícito na UI. */
  phoneUnavailable: boolean
}

/**
 * @param value valor cru
 * @returns `true` se o valor tem formato de BSUID
 */
export function isBsuid (value?: string | number | null): boolean {
  if (value === null || value === undefined) return false
  return BSUID_PATTERN.test(String(value).trim())
}

/**
 * Prefixo com cara de BSUID (`XX.` — duas letras e ponto). Para termos de
 * busca PARCIAIS, onde `isBsuid` não serve: `IN.164` ainda não é um bsuid
 * completo, mas já não é um telefone.
 * @param value termo de busca cru
 * @returns `true` se o termo começa como um BSUID
 */
export function looksLikeBsuid (value?: string | number | null): boolean {
  if (value === null || value === undefined) return false
  return /^[A-Za-z]{2}\./.test(String(value).trim())
}

/**
 * Normaliza o telefone para só dígitos.
 * @param phone telefone cru
 * @returns dígitos, ou `undefined` se não sobrar nenhum
 */
export function normalizePhone (phone?: string | number | null): string | undefined {
  if (phone === null || phone === undefined) return undefined
  const digits = String(phone).replace(/\D/g, '')
  return digits === '' ? undefined : digits
}

/**
 * Normaliza o bsuid — só `trim`, o formato é da Meta.
 * @param bsuid bsuid cru
 * @returns bsuid, ou `undefined` se vazio
 */
export function normalizeBsuid (bsuid?: string | null): string | undefined {
  if (bsuid === null || bsuid === undefined) return undefined
  const trimmed = String(bsuid).trim()
  return trimmed === '' ? undefined : trimmed
}

/**
 * Monta a identidade a partir de campos crus, no formato que a api-whatsapp
 * espera no corpo das requisições (chave ausente em vez de valor vazio).
 * @param input telefone e/ou bsuid
 * @returns identidade, ou `null` se não houver identificador utilizável
 */
export function makeIdentity (input?: { phone?: string | number | null; bsuid?: string | null } | null): WhatsappIdentity | null {
  const phone = normalizePhone(input?.phone)
  const bsuid = normalizeBsuid(input?.bsuid)

  if (phone) return bsuid ? { phone, bsuid } : { phone }
  if (bsuid) return { bsuid }
  return null
}

/**
 * Mascara o bsuid para exibição, preservando o prefixo de país e os 4 últimos
 * dígitos: `IN.1648738043044942` → `IN.••••4942`.
 *
 * Bsuid muito curto para mascarar é devolvido inteiro — melhor mostrar o valor
 * do que um `••••` que não identifica nada.
 * @param bsuid bsuid cru
 * @returns bsuid mascarado
 */
export function maskBsuid (bsuid?: string | null): string {
  const value = normalizeBsuid(bsuid)
  if (!value) return ''

  const match = value.match(/^([A-Za-z]{2})\.(\d+)$/)
  if (!match) return value

  const [, country, digits] = match
  if (digits.length <= 4) return value

  return `${country.toUpperCase()}.••••${digits.slice(-4)}`
}

export interface BotBsuidConfig {
  sendByBsuid: boolean
}

/**
 * Lê a configuração de BSUID do canal WhatsApp de um bot
 * (`integrations.whatsapp.bsuid`, persistida pelo drawer de instalação).
 *
 * Documento sem a chave = envio por BSUID desligado: o painel se comporta como
 * sempre. O gate é **só visual** — as APIs processam bsuid independentemente
 * desta config, então elementos orientados a dados (exibir o bsuid de um
 * contato que não tem telefone) NÃO devem ser condicionados por ela; só a
 * oferta de bsuid como opção de entrada/busca.
 *
 * **Não existe gate de recebimento.** Houve um `receiveWithoutPhone` aqui, que
 * nunca chegou a ser consultado por serviço nenhum — contato só-BSUID sempre
 * foi aceito na entrada. Removido em 2026-08-05 para não prometer um bloqueio
 * inexistente.
 * @param bot documento do bot (History), ex. `$store.state.main.botsById[id]`
 * @returns flags normalizadas para `boolean`
 */
export function botBsuidConfig (bot?: { integrations?: { whatsapp?: { bsuid?: { sendByBsuid?: boolean } } } } | null): BotBsuidConfig {
  const config = bot?.integrations?.whatsapp?.bsuid
  return {
    sendByBsuid: config?.sendByBsuid === true
  }
}

/**
 * Como exibir a identidade de um contato: telefone quando existe, bsuid
 * mascarado quando não.
 * @param doc documento com `phone` e/ou `bsuid` (conversation, room.whatsapp, bubble…)
 * @returns texto pronto e flags para a UI decidir ícone/aviso
 */
export function identityDisplay (doc?: { phone?: string | null; bsuid?: string | null } | null): IdentityDisplay {
  const phone = doc?.phone ? String(doc.phone).trim() : ''
  const bsuid = normalizeBsuid(doc?.bsuid)

  if (phone) {
    return {
      text: phone.startsWith('+') ? phone : `+${phone}`,
      isBsuid: false,
      phoneUnavailable: false
    }
  }

  if (bsuid) {
    return { text: maskBsuid(bsuid), isBsuid: true, phoneUnavailable: true }
  }

  return { text: '', isBsuid: false, phoneUnavailable: true }
}
